import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { DashboardProfileContextService, LegacyUserService } from '@rma/authentication/public/user';
import { CurrentSubscriptionService } from '@rma/billing/subscription-plans/public/api-subscription-features';
import { RmaModalService } from '@rma/generic/ui/modal';
import { RmaExperimentService } from '@rma/generic/util/experimentation';
import { FeatureFlagService, TempFeatureFlags } from '@rma/generic/util/feature-flags';
import { HeapCustomEventType, HeapEventService } from '@rma/generic/util/heap';
import { nonNullable } from '@rma/generic/util/operators/non-nullable';
import { SessionStorageService } from '@rma/generic/util/storage';
import { isWithinInterval, parseJSON, subMinutes } from 'date-fns';
import { combineLatest, filter, map, switchMap, take } from 'rxjs';

import { WelcomeModalComponent } from './welcome-modal.component';

@Injectable()
export class WelcomePrimerLoaderService {
  private readonly featuresService = inject(FeatureFlagService);
  private readonly experimentService = inject(RmaExperimentService);
  private readonly heapService = inject(HeapEventService);
  private readonly legacyUser = inject(LegacyUserService);
  private readonly modalService = inject(RmaModalService);
  private readonly profileContextService = inject(DashboardProfileContextService);
  private readonly router = inject(Router);
  private readonly sessionStorage = inject(SessionStorageService);
  private readonly subscriptionService = inject(CurrentSubscriptionService);

  public showWelcomeModal() {
    const claimedWithin$ = this.legacyUser.userInfo$.pipe(
      nonNullable(),
      map((userDetails) => {
        if (!userDetails.Created) {
          return false;
        }

        const createdDate = parseJSON(userDetails.Created);
        const end = subMinutes(new Date(), -2);
        const start = subMinutes(end, 15);
        return isWithinInterval(createdDate, { start, end });
      }),
    );

    const isEnabled$ = this.featuresService.getFeature$(TempFeatureFlags.EnableWelcomePrimerModal, false);
    const isIndividual$ = this.profileContextService.isIndividual$;
    const isSubscribed$ = this.subscriptionService.currentSubscription$.pipe(map((s) => s.isSubscribed || s.isProvided));
    const allowedRoutes$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((e) => !e.urlAfterRedirects.startsWith('/billing/')),
    );

    const sessionKey = 'rma_has_seen_welcome_modal';

    combineLatest([claimedWithin$, isSubscribed$, isIndividual$, isEnabled$, allowedRoutes$])
      .pipe(
        take(1),
        map(([claimedWithin, isSubscribed, isIndividual, isEnabled, allowedRoutes]) => {
          return isIndividual && claimedWithin && !isSubscribed && isEnabled && allowedRoutes;
        }),
        filter((show) => show && !this.sessionStorage.getBoolean(sessionKey)),
        switchMap(() => this.experimentService.getExperimentTreatment$<boolean>('Experiment.WelcomeModal').pipe(take(1))),
      )
      .subscribe((attract) => {
        this.sessionStorage.setBoolean(sessionKey, true);
        this.modalService.openFullScreenOnMobileOnly(WelcomeModalComponent, { data: attract });
        this.heapService.trackCustomEvent(HeapCustomEventType.WelcomeModalTest);
      });
  }
}
